<script setup lang="ts">
import { useQueries } from '@tanstack/vue-query'
import { computed, ref } from 'vue'
import { UploadedFileQueries } from '@/shared/queries'
import {
  faCircleCheck,
  faDownload,
  faExclamationTriangle,
  faTrashCan
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useFileUploadRemoval } from '@/shared/composables/useFileUploadRemoval'
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons'

type Ability = 'download' | 'delete'
type AllowedState = 'ok' | 'processing' | 'error'
type UploadedFile = (
  { state: 'loading', id: string } |
  { state: 'loading:failed', id: string } |
  { state?: AllowedState | 'removing', reason?: string, id: string, name: string, downloadUrl: string, abilities: Ability[] }
)

defineEmits<{ (e: 'delete', id: string): Promise<unknown> }>()

const props = defineProps<{
  uploads: (string|{ id: string, state?: AllowedState, reason?: string, abilities?: Ability[] })[]
  onDelete: (id: string) => Promise<unknown>
  tooltipPlacement?: 'top'|'bottom'|'left'|'right'
}>()

const { mutateAsync: removeUploadedFile } = useFileUploadRemoval()
const currentlyRemoving = ref<string[]>([])

const fileQueries = useQueries({
  queries: computed(
    () => props.uploads.map(x => UploadedFileQueries.fetch(typeof x === 'string' ? x : x.id))
  )
})

const fileList = computed<UploadedFile[]>(
  () => props.uploads.map(
    (upload, index) => {
      const id = typeof upload === 'string' ? upload : upload.id
      const state = typeof upload === 'string' ? undefined : upload.state
      const reason = typeof upload === 'string' ? undefined : upload.reason
      const abilities: Ability[] = typeof upload === 'string' ? ['delete', 'download'] : (upload.abilities || ['delete', 'download'])
      const query = fileQueries.value[index]

      // Either the query wasn't found, or there was an error with the query.
      if (query === undefined || query.isError) {
        return { id, state: 'loading:failed' }
      }

      // Busy fetching the details.
      if (query.isPending || query.isFetching) {
        return { id, state: 'loading' }
      }

      const { fileName, downloadUrl } = query.data

      return {
        id,
        state: currentlyRemoving.value.indexOf(id) > -1 ? 'removing' : state,
        reason,
        name: fileName,
        downloadUrl,
        abilities,
      }
    }
  )
)

function removeFile(id: string) {
  // Already removing it.
  if (currentlyRemoving.value.indexOf(id) > -1) {
    return
  }

  currentlyRemoving.value.push(id)

  props.onDelete(id).then(
    () => removeUploadedFile(id)
  ).finally(
    () => {
      const index = currentlyRemoving.value.indexOf(id)

      if (index > -1) {
        currentlyRemoving.value.splice(index, 1)
      }
    }
  )
}

</script>

<template>
  <div>
    <div class="d-flex align-items-center w-100" v-for="file in fileList" :key="file.id">
      <div :class="[$style.item, { [$style.removing]: file.state === 'removing' }]" class="flex-grow-1 justify-content-between d-flex align-items-center">
        <em v-if="file.state === 'loading'">Loading...</em>

        <template v-else-if="file.state === 'loading:failed'">
          <em>Failed to load.</em>
          <FontAwesomeIcon :icon="faExclamationTriangle" class="text-danger ps-2" v-tooltip="{ text: `This details of this file couldn't be loaded.`, placement: tooltipPlacement }" />
        </template>

        <template v-else>
          <span>{{ file.name }}</span>
          <span class="d-flex align-items-center gap-2">
            <a v-if="file.downloadUrl && file.abilities.includes('download')" target="_blank" :href="file.downloadUrl" class="d-block">
              <FontAwesomeIcon :icon="faDownload" />
            </a>
            <a href="javascript:void(0)" v-if="file.abilities.includes('delete')" class="d-inline-block" @click="removeFile(file.id)">
              <FontAwesomeIcon :icon="file.state === 'removing' ? faSpinnerThird : faTrashCan" :spin="file.state === 'removing'" />
            </a>
          </span>
        </template>
      </div>

      <div class="ps-2" v-if="file.state === 'ok'" v-tooltip="{ text: file.reason, placement: tooltipPlacement }">
        <FontAwesomeIcon :icon="faCircleCheck" class="text-success" />
      </div>

      <div class="ps-2" v-if="file.state === 'error'" v-tooltip="{ text: file.reason, placement: tooltipPlacement }">
        <FontAwesomeIcon v-if="file.state === 'error'" :icon="faExclamationTriangle" class="text-danger" />
      </div>

      <div class="ps-2" v-if="file.state === 'processing'" v-tooltip="{ text: file.reason || `Processing...\nYou'll be notified when done.`, placement: tooltipPlacement }">
        <FontAwesomeIcon :icon="faSpinnerThird" spin />
      </div>
    </div>
  </div>
</template>

<style module lang="scss">
@import "@/assets/variables";

.item {
  padding: 0 7px;
  background-color: #eee;

  a {
    color: #{$body-color};
  }

  &.removing {
    opacity: 0.5;
  }
}
</style>
